import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';
import { useNewsletter } from '../hooks/useNewsletter';
import { useSocialLinks } from '../hooks/useSocialLinks';

const logoColor = "/logo-color.svg";
const socialFb = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='%23679509'%3E%3Cpath d='M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z'/%3E%3C/svg%3E";
const socialTk = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='%23679509'%3E%3Cpath d='M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z'/%3E%3C/svg%3E";
const socialIg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23679509' stroke-width='2' stroke-linecap='round'%3E%3Crect x='2' y='2' width='20' height='20' rx='5'/%3E%3Ccircle cx='12' cy='12' r='5'/%3E%3Cline x1='17.5' y1='6.5' x2='17.51' y2='6.5'/%3E%3C/svg%3E";
const arrowSend = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round'%3E%3Cline x1='22' y1='2' x2='11' y2='13'/%3E%3Cpolygon points='22 2 15 22 11 13 2 9 22 2'/%3E%3C/svg%3E";

let boldLoaded = false;

export default function Footer() {
  const boldRef = useRef(null);
  const { email, setEmail, loading, message, success, subscribe } = useNewsletter();
  const socialLinks = useSocialLinks();

  useEffect(() => {
    if (boldLoaded || !boldRef.current) return;
    boldLoaded = true;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        observer.disconnect();
        const script = document.createElement('script');
        script.src = 'https://bold.co/library/ui-kit.js?type=slider';
        boldRef.current.appendChild(script);
      }
    }, { rootMargin: '200px' });
    observer.observe(boldRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <footer className="pie">
      <div className="pie__grid">
        {/* Brand */}
        <div className="pie__brand">
          <img className="pie__logo" src={logoColor} alt="Cuponeo" />
          <p className="pie__brand-text">
            Conectamos a las personas con las mejores ofertas de su ciudad.
          </p>
          <div className="pie__socials">
            {socialLinks.facebook && <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="pie__social-link"><img src={socialFb} alt="Facebook" /></a>}
            {socialLinks.tiktok && <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="pie__social-link"><img src={socialTk} alt="TikTok" /></a>}
            {socialLinks.instagram && <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="pie__social-link"><img src={socialIg} alt="Instagram" /></a>}
          </div>
        </div>

        {/* Explorar */}
        <div className="pie__col">
          <h4 className="pie__col-title">Explorar</h4>
          <nav className="pie__links">
            <Link to="/ayuda" className="pie__link">Sobre Nosotros</Link>
            <Link to="/ofertas" className="pie__link">Cupones destacados</Link>
          </nav>
        </div>

        {/* Soporte */}
        <div className="pie__col">
          <h4 className="pie__col-title">Soporte</h4>
          <nav className="pie__links">
            <Link to="/ayuda" className="pie__link">Centro de Ayuda</Link>
            <Link to="/ayuda?tab=contact" className="pie__link">Contacto</Link>
            <Link to="/privacidad" className="pie__link">Política de Privacidad</Link>
            <Link to="/terminos" className="pie__link">Términos de Servicio</Link>
            <Link to="/cookies" className="pie__link">Política de Cookies</Link>
          </nav>
        </div>

        {/* Newsletter */}
        <div className="pie__newsletter">
          <h4 className="pie__newsletter-title">Mantente al Día</h4>
          <p className="pie__newsletter-desc">Suscríbete a nuestro newsletter editorial.</p>
          <form className="pie__newsletter-form" onSubmit={subscribe}>
            <input
              className="pie__newsletter-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={loading}
            />
            <button className="pie__newsletter-btn" type="submit" disabled={loading}>
              <img src={arrowSend} alt="Enviar" />
            </button>
          </form>
          {message && (
            <p style={{ fontSize: '12px', marginTop: '8px', color: success ? '#16a34a' : '#dc2626' }}>
              {message}
            </p>
          )}
        </div>
      </div>

      {/* Bottom */}
      <div className="pie__bottom">
        <p className="pie__copyright">
          © {new Date().getFullYear()} Cuponeo. Los mejores descuentos de Colombia.
        </p>
        <div className="pie__payment-icons" ref={boldRef} />
      </div>
    </footer>
  );
}
